'use client'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'

import { CellTokenAmountPrice } from '@morpho-org/ui-v6/client'
import { CellAllocation, Skeleton } from '@morpho-org/ui-v6/server'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import { rowSortingFeature, tableFeatures } from '@tanstack/react-table'

import { HealthFactorCell } from '@/components/common/Cells/HealthFactorCell'
import { UserCellDashboard } from '@/components/common/Cells/UserCell'

import type { MarketBorrowersTableRow } from './MarketBorrowers.types'

export const marketBorrowersTableFeatures = tableFeatures({
  rowSortingFeature
})
export type MarketBorrowersTableFeatures = typeof marketBorrowersTableFeatures

export const marketBorrowersColumnsDefs: ColumnDefWithSkeleton<
  MarketBorrowersTableFeatures,
  MarketBorrowersTableRow
>[] = [
  {
    id: 'borrower',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '7px' }}>
            Borrower
          </span>
        </TooltipTrigger>
        <TooltipContent>Address of the wallet that borrowed from the market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { borrower } = row.original
      return <UserCellDashboard address={borrower.address} />
    },

    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[144px] rounded-[20px]" />
  },
  {
    id: 'totalCollateral',
    accessorFn: row => row.collateral.amount,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Collateral</span>
        </TooltipTrigger>
        <TooltipContent>Amount of collateral assets deposited.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { collateral } = row.original
      return (
        <CellTokenAmountPrice
          token={collateral.asset}
          amount={collateral.amount}
          value={collateral.amountUsd}
        />
      )
    },
    enableSorting: true,
    skeletonCell: <Skeleton className="h-[22px] w-[195px] rounded-[20px]" />
  },
  {
    id: 'totalLoan',
    accessorFn: row => row.loan.amount,
    header: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground">Loan</span>
          </TooltipTrigger>
          <TooltipContent>Amount borrowed from the market.</TooltipContent>
        </Tooltip>
      )
    },
    cell: ({ row }) => {
      const { loan } = row.original
      return <CellTokenAmountPrice token={loan.asset} amount={loan.amount} value={loan.amountUsd} />
    },
    enableSorting: true,
    skeletonCell: <Skeleton className="h-[22px] w-[195px] rounded-[20px]" />
  },
  {
    id: 'health',
    accessorFn: row => row.health.healthFactor,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '14px' }}>
            Health
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Position&apos;s loan-to-value ratio (LTV) compared to its liquidation threshold (LLTV).
          Liquidation occurs if health factor reaches 1.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { healthFactor, ltv, lltv, decimals, borrowShares, market, collateral } =
        row.original.health
      return (
        <HealthFactorCell
          data={{
            health: {
              healthFactor,
              ltv,
              lltv,
              decimals
            },
            collateral,
            borrowShares,
            market
          }}
          chainId={row.original.borrower.chainId}
        />
      )
    },
    skeletonCell: <Skeleton className="h-[22px] w-[102px] rounded-[20px]" />,
    enableSorting: true
  },
  {
    id: 'borrowShare',
    accessorFn: row => row.borrowShare.value,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Borrow Share</span>
        </TooltipTrigger>
        <TooltipContent>
          Percentage of the market&apos;s outstanding loans held by the borrower.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { value, percentageOffset } = row.original.borrowShare
      return (
        <CellAllocation allocation={value} percentageOffset={percentageOffset} index={row.index} />
      )
    },
    skeletonCell: <Skeleton className="h-[22px] w-[68px] rounded-[20px]" />,
    enableSorting: true
  }
]
